import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Edit2, Check, X, Loader2 } from "lucide-react";
import LoaderComponent from "@/components/common/LoaderComponent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FilterSection } from "../../../components/patients/FilterSection";
import { PatientListTable } from "../../../components/patients/PatientListTable";
import { FilterModal } from "../../../components/patients/FilterModal";
import { useToast } from "@/hooks/use-toast";
import { usePatientDetailsForGroups } from "@/hooks/patient/usePatientDetailsForGroups";
import { usePatientGroups } from "@/hooks/activity/usePatientGroups";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { API_CONFIG } from "@/config/envConfig";

export default function EditPatientGroup() {
  const { patientGroupId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, token } = useAuth();
  const { patientGroups, loading: groupsLoading, refetch: refetchGroups } = usePatientGroups();

  // Filter states
  const [nameValue, setNameValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [emrValue, setEmrValue] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [selectCount, setSelectCount] = useState<number>(0);

  const itemsPerPage = 10;

  // Get current group data
  const currentGroup = useMemo(() => {
    return patientGroups.find(g => g.patient_group_id.toString() === patientGroupId);
  }, [patientGroups, patientGroupId]);

  // Group name editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedGroupName, setEditedGroupName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  // Fetch patients for this specific group
  const { 
    patients, 
    isLoading, 
    totalCount, 
    refetch 
  } = usePatientDetailsForGroups(
    itemsPerPage, 
    (currentPage - 1) * itemsPerPage,
    patientGroupId ? parseInt(patientGroupId) : undefined
  );

  // Set group name when data loads
  useEffect(() => {
    if (currentGroup) {
      setEditedGroupName(currentGroup.group_name || '');
    }
  }, [currentGroup]);

  // Refetch data when returning to this page
  useEffect(() => {
    const handleFocus = () => {
      refetch();
      refetchGroups();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch, refetchGroups]);

  // Transform patients data for display
  const displayPatients = useMemo(() => {
    return patients.map(patient => ({
      id: patient.enrollment_patient_id?.toString() || '',
      patientName: `${patient.first_name || ''} ${patient.middle_name || ''} ${patient.last_name || ''}`.trim(),
      emr: patient.emr_id || 'N/A',
      gender: patient.gender || 'N/A',
      dateOfBirth: patient.dob || 'N/A',
      age: patient.dob ? Math.floor((new Date().getTime() - new Date(patient.dob).getTime()) / 31557600000) : 0,
      phoneNumber: patient.mobile_number || patient.home_number || 'N/A',
      providerDetails: patient.provider_name || 'N/A',
      clinicDetails: patient.clinic_name || 'N/A',
      primaryDx: 'N/A',
      secondaryDx: 'N/A',
      insuranceType: 'N/A',
    }));
  }, [patients]);

  // Pagination logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSearch = () => {
    setCurrentPage(1);
    const searchFilters: any = {};
    
    // Build basic search filters
    const conditions: any[] = [];
    
    if (nameValue) {
      conditions.push({
        _or: [
          { first_name: { _ilike: `%${nameValue}%` } },
          { last_name: { _ilike: `%${nameValue}%` } }
        ]
      });
    }
    if (phoneValue) {
      conditions.push({ mobile_number: { _ilike: `%${phoneValue}%` } });
    }
    if (emrValue) {
      conditions.push({ emr_id: { _ilike: `%${emrValue}%` } });
    }
    
    // Add advanced filters from FilterModal
    if (appliedFilters.clinic_id?.length > 0) {
      conditions.push({ clinic_id: { _in: appliedFilters.clinic_id } });
    }
    if (appliedFilters.clinic_facility_id?.length > 0) {
      conditions.push({ clinic_facility_id: { _in: appliedFilters.clinic_facility_id } });
    }
    if (appliedFilters.provider_id?.length > 0) {
      conditions.push({ provider_id: { _in: appliedFilters.provider_id } });
    }
    if (appliedFilters.primary_insurance_payor?.length > 0) {
      conditions.push({ primary_insurance_payor: { _in: appliedFilters.primary_insurance_payor } });
    }
    if (appliedFilters.secondary_insurance_payor?.length > 0) {
      conditions.push({ secondary_insurance_payor: { _in: appliedFilters.secondary_insurance_payor } });
    }
    if (appliedFilters.condition?.length > 0) {
      conditions.push({ condition: { _in: appliedFilters.condition } });
    }
    if (appliedFilters.zip_code?.length > 0) {
      conditions.push({ zip_code: { _in: appliedFilters.zip_code } });
    }
    if (appliedFilters.program_eligibility?.length > 0) {
      conditions.push({ program_eligibility: { _in: appliedFilters.program_eligibility } });
    }
    
    // Combine all conditions
    if (conditions.length > 0) {
      searchFilters._and = conditions;
    }
    
    refetch(searchFilters);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleClearFilters = () => {
    setNameValue("");
    setPhoneValue("");
    setEmrValue("");
    setAppliedFilters({});
    setCurrentPage(1);
    refetch({});
  };

  const handlePatientSelect = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedPatients(prev => [...prev, patientId]);
    } else {
      setSelectedPatients(prev => prev.filter(id => id !== patientId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const allCurrentPageIds = displayPatients.map(p => p.id);
    
    if (checked) {
      setSelectedPatients(prev => [...new Set([...prev, ...allCurrentPageIds])]);
    } else {
      setSelectedPatients(prev => prev.filter(id => !allCurrentPageIds.includes(id)));
    }
  };

  const handleRemovePatients = () => {
    navigate(`/admin/patient-groups/edit/${patientGroupId}/remove-patients`);
  };

  const handleAddPatients = () => {
    navigate(`/admin/patient-groups/edit/${patientGroupId}/add-patients`);
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editedGroupName.trim()) {
      toast({
        title: "Error",
        description: "Group name cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    if (!currentGroup || !user || !token) {
      toast({
        title: "Error",
        description: "Missing required information to update group name.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingName(true);

    try {
      const payload = {
        groupName: editedGroupName.trim(),
        isArchived: currentGroup.is_archived || false,
        patientIdsToAdd: [],
        patientIdsToRemove: []
      };

      const url = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/${patientGroupId}`;
      
      await axios.put(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      setIsEditingName(false);
      
      toast({
        title: "Success",
        description: "Group name updated successfully.",
      });

      // Refetch groups to update the list
      refetchGroups();
    } catch (error) {
      console.error('Error updating group name:', error);
      toast({
        title: "Error",
        description: "Failed to update group name.",
        variant: "destructive",
      });
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    if (currentGroup) {
      setEditedGroupName(currentGroup.group_name || '');
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/patient-groups")}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">{currentGroup?.group_name || 'Loading...'}</h1>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {(groupsLoading || isLoading) ? (
          <LoaderComponent size="md" text="Loading patient group data..." />
        ) : (
        <>
        {/* Group Info */}
        <div className="bg-background border rounded-lg p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Group Name</span>
              </div>
              
              {isEditingName ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={editedGroupName}
                    onChange={(e) => setEditedGroupName(e.target.value)}
                    className="max-w-xs"
                    placeholder="Enter group name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName();
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveName}
                    disabled={isSavingName}
                    className="px-2 py-1 h-8"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="px-2 py-1 h-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <h2 className="text-lg font-semibold text-foreground">{currentGroup?.group_name || 'Loading...'}</h2>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleEditName}
                    className="px-2 py-1 h-8"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-background border rounded-lg p-6">
          <FilterSection
            nameValue={nameValue}
            phoneValue={phoneValue}
            emrValue={emrValue}
            onNameChange={setNameValue}
            onPhoneChange={setPhoneValue}
            onEmrChange={setEmrValue}
            onSearch={handleSearch}
            onFilter={handleFilter}
          />
          
          {/* Clear Filters Button */}
          {(nameValue || phoneValue || emrValue || Object.keys(appliedFilters).length > 0) && (
            <div className="mt-4 flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleClearFilters}
                className="text-destructive hover:text-destructive"
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        {/* Patients Section */}
        <div className="bg-background border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">
              Patients in {currentGroup?.group_name || 'Group'} ({totalCount})
            </h3>
            <div className="flex gap-3">
              <Button 
                variant="destructive" 
                onClick={handleRemovePatients}
                className="w-40"
              >
                Remove Patients
              </Button>
              <Button 
                onClick={handleAddPatients}
                className="w-40 bg-amber-500 hover:bg-amber-600 text-white"
              >
                Add Patients
              </Button>
            </div>
          </div>

          <PatientListTable
            patients={displayPatients}
            selectedPatients={selectedPatients}
            onPatientSelect={handlePatientSelect}
            onSelectAll={handleSelectAll}
            selectCount={selectCount}
            onSelectCountChange={setSelectCount}
            filteredCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
        </>
        )}
      </main>

      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        initialFilters={appliedFilters}
        onApply={(filters) => {
          setAppliedFilters(filters);
          setIsFilterModalOpen(false);
          // Trigger search after filters are applied
          setTimeout(() => handleSearch(), 100);
        }}
      />
    </div>
  );
}